import { v4 as uuid } from "uuid";

import {
  HK_ToolPaletteMarkerId,
  HK_ToolPaletteMiscId,
  HK_ToolPalettePlayerId,
  HK_ToolPaletteStencilId,
  isHKMiscId,
  isHKPlayerId,
  isHKPuckId,
} from "../../config";
import {
  ActionType,
  FrameStencil,
  HockeyActionLineType,
  HockeyMiscType,
  HockeyPlayerLabelType,
  MarkerBase,
  MarkerType,
  Player,
  Point,
  StencilType,
} from "../../types";

const getMarkerType = (id: HK_ToolPaletteMarkerId): MarkerType => {
  if (isHKPlayerId(id)) return MarkerType.PLAYER;
  else if (isHKPuckId(id)) return MarkerType.BALL;
  else if (isHKMiscId(id)) return MarkerType.MISC;
  else if (id === "MISC_TEXTBOX") return MarkerType.TEXT;
  else if (id === "SHADING_RECTANGLE") return MarkerType.SHADING_RECTANGLE;
  else if (id === "SHADING_OVAL") return MarkerType.SHADING_OVAL;
  else if (id === "SHADING_TRAPEZOID") return MarkerType.SHADING_TRAPEZOID;
  else throw new Error(`Invalid BB_ToolPaletteMarkerId: ${id}`);
};

const getPlayerIdNumber = (id: HK_ToolPalettePlayerId): Player["playerIdNumber"] => {
  switch (id) {
    case "HK_PLAYER_F_1":
    case "HK_PLAYER_POSSESSION_F_1":
    case "HK_PLAYER_FILL_F_1":
      return 1;

    case "HK_PLAYER_F_2":
    case "HK_PLAYER_POSSESSION_F_2":
    case "HK_PLAYER_FILL_F_2":
      return 2;

    case "HK_PLAYER_F_3":
    case "HK_PLAYER_POSSESSION_F_3":
    case "HK_PLAYER_FILL_F_3":
      return 3;

    case "HK_PLAYER_D_1":
    case "HK_PLAYER_POSSESSION_D_1":
    case "HK_PLAYER_FILL_D_1":
      return 4;

    case "HK_PLAYER_D_2":
    case "HK_PLAYER_POSSESSION_D_2":
    case "HK_PLAYER_FILL_D_2":
      return 5;

    case "HK_PLAYER_G":
    case "HK_PLAYER_POSSESSION_G":
    case "HK_PLAYER_FILL_G":
      return 6;
  }
};

const getPlayerLabel = (id: HK_ToolPalettePlayerId, labelType: HockeyPlayerLabelType): Player["label"] => {
  const isLR = labelType === "LR";
  switch (id) {
    case "HK_PLAYER_F_1":
    case "HK_PLAYER_POSSESSION_F_1":
    case "HK_PLAYER_FILL_F_1":
      return isLR ? "LW" : "F1";

    case "HK_PLAYER_F_2":
    case "HK_PLAYER_POSSESSION_F_2":
    case "HK_PLAYER_FILL_F_2":
      return isLR ? "C" : "F2";

    case "HK_PLAYER_F_3":
    case "HK_PLAYER_POSSESSION_F_3":
    case "HK_PLAYER_FILL_F_3":
      return isLR ? "RW" : "F3";

    case "HK_PLAYER_D_1":
    case "HK_PLAYER_POSSESSION_D_1":
    case "HK_PLAYER_FILL_D_1":
      return isLR ? "LD" : "D1";

    case "HK_PLAYER_D_2":
    case "HK_PLAYER_POSSESSION_D_2":
    case "HK_PLAYER_FILL_D_2":
      return isLR ? "RD" : "D2";

    case "HK_PLAYER_G":
    case "HK_PLAYER_POSSESSION_G":
    case "HK_PLAYER_FILL_G":
      return "G";
  }
};

const getPlayerFill = (id: HK_ToolPalettePlayerId): Player["fill"] => {
  switch (id) {
    case "HK_PLAYER_FILL_F_1":
    case "HK_PLAYER_FILL_F_2":
    case "HK_PLAYER_FILL_F_3":
    case "HK_PLAYER_FILL_D_1":
    case "HK_PLAYER_FILL_D_2":
    case "HK_PLAYER_FILL_G":
      return true;

    case "HK_PLAYER_F_1":
    case "HK_PLAYER_F_2":
    case "HK_PLAYER_F_3":
    case "HK_PLAYER_D_1":
    case "HK_PLAYER_D_2":
    case "HK_PLAYER_G":
    case "HK_PLAYER_POSSESSION_F_1":
    case "HK_PLAYER_POSSESSION_F_2":
    case "HK_PLAYER_POSSESSION_F_3":
    case "HK_PLAYER_POSSESSION_D_1":
    case "HK_PLAYER_POSSESSION_D_2":
    case "HK_PLAYER_POSSESSION_G":
      return false;
  }
};

const playerHasPossession = (id: HK_ToolPalettePlayerId): boolean => {
  switch (id) {
    case "HK_PLAYER_POSSESSION_F_1":
    case "HK_PLAYER_POSSESSION_F_2":
    case "HK_PLAYER_POSSESSION_F_3":
    case "HK_PLAYER_POSSESSION_D_1":
    case "HK_PLAYER_POSSESSION_D_2":
    case "HK_PLAYER_POSSESSION_G":
      return true;

    case "HK_PLAYER_F_1":
    case "HK_PLAYER_F_2":
    case "HK_PLAYER_F_3":
    case "HK_PLAYER_D_1":
    case "HK_PLAYER_D_2":
    case "HK_PLAYER_G":
    case "HK_PLAYER_FILL_F_1":
    case "HK_PLAYER_FILL_F_2":
    case "HK_PLAYER_FILL_F_3":
    case "HK_PLAYER_FILL_D_1":
    case "HK_PLAYER_FILL_D_2":
    case "HK_PLAYER_FILL_G":
      return false;
  }
};

const getMiscType = (id: HK_ToolPaletteMiscId): HockeyMiscType => {
  switch (id) {
    case "MISC_PUCK":
      return HockeyMiscType.PUCK_STATIC;
    case "MISC_CONE":
      return HockeyMiscType.CONE;
    case "MISC_FLAG":
      return HockeyMiscType.FLAG;
    case "MISC_TIRE":
      return HockeyMiscType.TIRE;
    case "MISC_STICK_RIGHT":
      return HockeyMiscType.STICK_RIGHT;
    case "MISC_STICK_LEFT":
      return HockeyMiscType.STICK_LEFT;
    case "MISC_TRIANGLE_STICK":
      return HockeyMiscType.TRIANGLE_STICK;
    case "MISC_BOARD":
      return HockeyMiscType.BOARD;
    case "MISC_PUCK_PILE":
      return HockeyMiscType.PILE;
    case "MISC_NET":
      return HockeyMiscType.NET;
  }
};

export interface CreateHockeyStencilFromToolPaletteProps {
  id: HK_ToolPaletteStencilId;
  positionLabelType: HockeyPlayerLabelType;
  pos: Point;
}
export const createHockeyStencilFromToolPalette = ({
  id,
  positionLabelType,
  pos,
}: CreateHockeyStencilFromToolPaletteProps): FrameStencil | null => {
  const stencilId = uuid();
  if (id === "DHO")
    return {
      id: stencilId,
      stencilType: StencilType.ACTION,
      actionType: ActionType.HANDOFF,
      layerType: HockeyActionLineType.HANDOFF,
      pos,
      midPoints: [],
      startPos: pos,
      endPos: pos,
      startTime: 0,
      endTime: 0,
      prevPlayer: null,
      nextPlayer: null,
      prevAction: null,
      nextAction: null,
    };

  const markerType = getMarkerType(id);
  const markerBase: MarkerBase = { id: stencilId, pos, stencilType: StencilType.MARKER };

  switch (markerType) {
    case MarkerType.PLAYER: {
      if (!isHKPlayerId(id)) return null;
      return {
        ...markerBase,
        markerType,
        playerIdNumber: getPlayerIdNumber(id),
        label: getPlayerLabel(id, positionLabelType),
        fill: getPlayerFill(id),
        possession: playerHasPossession(id),
        color: "#000",
        movements: [],
      };
    }

    case MarkerType.BALL:
      return { ...markerBase, markerType, associatedPlayer: "", movements: [] };

    case MarkerType.MISC: {
      if (!isHKMiscId(id)) return null;
      return { ...markerBase, markerType, miscType: getMiscType(id) };
    }

    case MarkerType.TEXT:
      return { ...markerBase, markerType, label: "text", width: 20 };

    case MarkerType.SHADING_RECTANGLE:
      return { ...markerBase, markerType, width: 5, height: 10 };

    case MarkerType.SHADING_OVAL:
      return { ...markerBase, markerType, rx: 2.5, ry: 5 };

    case MarkerType.SHADING_TRAPEZOID: {
      const { x, y } = pos;
      return {
        ...markerBase,
        markerType,
        shadeVertices: [
          { x, y },
          { x: x + 10, y },
          { x: x + 7.5, y: y + 10 },
          { x: x + 2.5, y: y + 10 },
        ],
      };
    }

    default:
      return null;
  }
};
